import React from 'react';
import Card from '@/components/ui/Card';
import {
  PlayerComparisonV2Benchmark,
  PlayerComparisonV2Item,
  PlayerComparisonWyscoutMetrics,
} from '@/features/players/api';

type MetricKey = keyof PlayerComparisonWyscoutMetrics;
type MetricKind = 'total' | 'per90' | 'percent';

interface MetricDefinition {
  readonly key: MetricKey;
  readonly label: string;
  readonly kind: MetricKind;
  readonly formatter: (value: number | null) => string;
}

interface MetricGroup {
  readonly label: string;
  readonly visualMetrics: readonly MetricDefinition[];
  readonly tableMetrics: readonly MetricDefinition[];
}

const totalFormatter = new Intl.NumberFormat('en-GB', {maximumFractionDigits: 2});
const rateFormatter = new Intl.NumberFormat('en-GB', {maximumFractionDigits: 2});
const percentFormatter = new Intl.NumberFormat('en-GB', {minimumFractionDigits: 1, maximumFractionDigits: 1});

const formatTotal = (value: number | null) => value === null ? '—' : totalFormatter.format(value);
const formatRate = (value: number | null) => value === null ? '—' : rateFormatter.format(value);
const formatPercent = (value: number | null) => value === null ? '—' : `${percentFormatter.format(value)}%`;

const metric = (key: MetricKey, label: string, kind: MetricKind): MetricDefinition => Object.freeze({
  key,
  label,
  kind,
  formatter: kind === 'percent' ? formatPercent : kind === 'per90' ? formatRate : formatTotal,
});

const metricGroups = [
  {
    label: 'Sample',
    visualMetrics: [
      metric('appearances', 'Appearances', 'total'),
      metric('minutes', 'Minutes', 'total'),
    ],
    tableMetrics: [
      metric('sample_size', 'Sample size', 'total'),
      metric('appearances', 'Appearances', 'total'),
      metric('minutes', 'Minutes', 'total'),
    ],
  },
  {
    label: 'Attacking',
    visualMetrics: [
      metric('goals_per_90', 'Goals Per 90', 'per90'),
      metric('assists_per_90', 'Assists Per 90', 'per90'),
      metric('shots_per_90', 'Shots Per 90', 'per90'),
      metric('shots_on_target_per_90', 'Shots on target Per 90', 'per90'),
      metric('expected_goals_per_90', 'Expected goals Per 90', 'per90'),
      metric('expected_assists_per_90', 'Expected assists Per 90', 'per90'),
    ],
    tableMetrics: [
      metric('goals', 'Goals Total', 'total'),
      metric('goals_per_90', 'Goals Per 90', 'per90'),
      metric('assists', 'Assists Total', 'total'),
      metric('assists_per_90', 'Assists Per 90', 'per90'),
      metric('shots', 'Shots Total', 'total'),
      metric('shots_per_90', 'Shots Per 90', 'per90'),
      metric('shots_on_target', 'Shots on target Total', 'total'),
      metric('shots_on_target_per_90', 'Shots on target Per 90', 'per90'),
      metric('expected_goals', 'Expected goals Total', 'total'),
      metric('expected_goals_per_90', 'Expected goals Per 90', 'per90'),
      metric('expected_assists', 'Expected assists Total', 'total'),
      metric('expected_assists_per_90', 'Expected assists Per 90', 'per90'),
    ],
  },
  {
    label: 'Possession',
    visualMetrics: [
      metric('passes_per_90', 'Passes Per 90', 'per90'),
      metric('successful_passes_per_90', 'Successful passes Per 90', 'per90'),
      metric('pass_completion', 'Pass completion', 'percent'),
    ],
    tableMetrics: [
      metric('passes', 'Passes Total', 'total'),
      metric('passes_per_90', 'Passes Per 90', 'per90'),
      metric('successful_passes', 'Successful passes Total', 'total'),
      metric('successful_passes_per_90', 'Successful passes Per 90', 'per90'),
      metric('pass_completion', 'Pass completion', 'percent'),
    ],
  },
  {
    label: 'Duels and defending',
    visualMetrics: [
      metric('duels_per_90', 'Duels Per 90', 'per90'),
      metric('duels_won_per_90', 'Duels won Per 90', 'per90'),
      metric('duel_success', 'Duel success', 'percent'),
      metric('interceptions_per_90', 'Interceptions Per 90', 'per90'),
      metric('recoveries_per_90', 'Recoveries Per 90', 'per90'),
    ],
    tableMetrics: [
      metric('duels', 'Duels Total', 'total'),
      metric('duels_per_90', 'Duels Per 90', 'per90'),
      metric('duels_won', 'Duels won Total', 'total'),
      metric('duels_won_per_90', 'Duels won Per 90', 'per90'),
      metric('duel_success', 'Duel success', 'percent'),
      metric('interceptions', 'Interceptions Total', 'total'),
      metric('interceptions_per_90', 'Interceptions Per 90', 'per90'),
      metric('recoveries', 'Recoveries Total', 'total'),
      metric('recoveries_per_90', 'Recoveries Per 90', 'per90'),
    ],
  },
] as const satisfies readonly MetricGroup[];

const playerBarColors = ['bg-blue-700 dark:bg-blue-400', 'bg-fuchsia-700 dark:bg-fuchsia-400', 'bg-emerald-700 dark:bg-emerald-400', 'bg-orange-700 dark:bg-orange-400'] as const;

interface Series {
  readonly key: string;
  readonly label: string;
  readonly metrics: PlayerComparisonWyscoutMetrics | null;
  readonly barClass: string;
  readonly benchmark: boolean;
}

const WyscoutPerformanceProfile: React.FC<{
  players: readonly PlayerComparisonV2Item[];
  benchmarks: readonly PlayerComparisonV2Benchmark[];
}> = ({players, benchmarks}) => {
  const series: readonly Series[] = [
    ...players.map((player, index) => ({
      key: player.id,
      label: player.name,
      metrics: player.wyscout,
      barClass: playerBarColors[index % playerBarColors.length],
      benchmark: false,
    })),
    ...benchmarks.map(benchmark => ({
      key: `benchmark-${benchmark.position}-${benchmark.scope}`,
      label: `${benchmark.position} ${benchmark.scope} benchmark`,
      metrics: benchmark.wyscout,
      barClass: 'border-2 border-dashed border-gray-700 bg-gray-300 dark:border-gray-200 dark:bg-gray-600',
      benchmark: true,
    })),
  ];
  const displayValue = (item: Series, key: MetricKey): number | null => {
    if (!item.metrics || (key !== 'sample_size' && item.metrics.sample_size <= 0)) return null;
    const value = item.metrics[key];
    return typeof value === 'number' && Number.isFinite(value) ? value : null;
  };
  const hasSamples = series.some(item => (displayValue(item, 'sample_size') ?? 0) > 0);

  return (
    <Card className="p-0">
      <section aria-labelledby="wyscout-performance-heading">
        <header className="border-b border-gray-200 p-4 dark:border-gray-700 sm:p-6">
          <h2 id="wyscout-performance-heading" className="text-xl font-semibold">Wyscout performance profile</h2>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">Per-90 rates and source totals for the selected players and position benchmarks.</p>
        </header>

        <div className="space-y-8 p-4 sm:p-6">
          {!hasSamples && (
            <p className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-gray-700 dark:border-gray-700 dark:bg-gray-800/60 dark:text-gray-200">
              No valid Wyscout samples are available for this comparison.
            </p>
          )}
          {metricGroups.map(group => {
            const hasVisualValues = group.visualMetrics.some(definition => series.some(item => displayValue(item, definition.key) !== null));
            return (
              <section key={group.label} aria-labelledby={`wyscout-${group.label.replaceAll(' ', '-').toLowerCase()}`}>
                <h3 id={`wyscout-${group.label.replaceAll(' ', '-').toLowerCase()}`} className="mb-3 text-lg font-semibold">{group.label}</h3>
                {!hasVisualValues ? (
                  <p className="text-sm text-gray-600 dark:text-gray-300">No {group.label} comparison values are available.</p>
                ) : (
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                  {group.visualMetrics.map(definition => {
                    const values = series.map(item => displayValue(item, definition.key));
                    const maximum = Math.max(1, ...values.filter((value): value is number => value !== null));
                    return (
                      <figure key={definition.key} className="min-w-0 rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800/60">
                        <figcaption className="font-semibold">{definition.label}</figcaption>
                        <div className="mt-3 space-y-3">
                          {series.map((item, index) => {
                            const value = values[index];
                            const formatted = item.metrics ? definition.formatter(value) : 'No Wyscout data';
                            const width = value === null ? 0 : Math.max(0, value / maximum * 100);
                            return (
                              <div key={item.key} data-testid="wyscout-metric-row" className="grid min-w-0 grid-cols-1 gap-2 text-sm sm:grid-cols-[minmax(0,2fr)_minmax(0,3fr)_auto] sm:items-center">
                                <span className="min-w-0 break-words font-medium">{item.label}</span>
                                <span className="h-3 w-full min-w-0 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700" aria-hidden="true">
                                  {value !== null && <span data-testid="wyscout-bar" className={`block h-full rounded-full ${item.barClass}`} style={{width: `${width}%`}} />}
                                </span>
                                <span data-metric-value aria-label={`${item.label}, ${definition.label}`} className={`min-w-0 tabular-nums sm:text-right ${item.benchmark ? 'font-semibold' : ''}`}>{formatted}</span>
                              </div>
                            );
                          })}
                        </div>
                      </figure>
                    );
                  })}
                </div>
                )}
              </section>
            );
          })}
        </div>

        <div className="overflow-x-auto border-t border-gray-200 dark:border-gray-700">
          <table aria-label="Wyscout performance profile data" className="min-w-full text-left text-sm">
            <thead className="bg-gray-100 dark:bg-gray-800">
              <tr>
                <th scope="col" className="px-4 py-3 font-semibold">Metric</th>
                {series.map(item => <th key={item.key} scope="col" className="min-w-40 break-words px-4 py-3 font-semibold">{item.label}</th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {metricGroups.flatMap(group => group.tableMetrics.map(definition => (
                <tr key={definition.key}>
                  <th scope="row" className="whitespace-nowrap px-4 py-3 font-medium">{definition.label}</th>
                  {series.map(item => (
                    <td key={item.key} className="px-4 py-3 tabular-nums">
                      {item.metrics ? definition.formatter(displayValue(item, definition.key)) : 'No Wyscout data'}
                    </td>
                  ))}
                </tr>
              )))}
            </tbody>
          </table>
        </div>
      </section>
    </Card>
  );
};

export default WyscoutPerformanceProfile;
